import React, { useState } from 'react';
import styled from 'styled-components';
import { Card as BaseCard } from '../../../../styles/components';
import { glassDeep } from '../../../../styles/components/common/glassmorphism';
import { Button } from '../../../../styles/components/common/buttons';

const Card = styled(BaseCard)`
  height: 100%;
  padding: ${({ theme }) => theme.spacing.xl};
  ${glassDeep}
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      120deg,
      transparent, 
      transparent,
      rgba(99, 102, 241, 0.05),
      transparent,
      transparent
    );
    background-size: 200% 100%;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }
  
  &:hover:before {
    opacity: 1;
  }
`;

const Title = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.textOnDark};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  transition: color 0.3s ease;
  
  ${Card}:hover & {
    color: ${({ theme }) => theme.colors.primaryLight};
  }
`;

const CompletedDate = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textOnDark};
  opacity: 0.8;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.5rem;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl};
`;

const WorkshopDetails = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  h4 {
    font-size: ${({ theme }) => theme.typography.fontSize.md};
    color: ${({ theme }) => theme.colors.textOnDark};
    margin: 0 0 ${({ theme }) => theme.spacing.sm};
    opacity: 0.9;
  }
  
  p {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.textOnDark};
    line-height: ${({ theme }) => theme.typography.lineHeight.normal};
    opacity: 0.8;
    margin: 0 0 ${({ theme }) => theme.spacing.md};
  }
`;

const PriceTag = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ isFree }) => isFree ? 'rgba(16, 185, 129, 0.9)' : 'rgba(255, 255, 255, 0.9)'};
  font-weight: 500;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.25rem;
  }
`;

const CompletedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  background: ${({ theme }) => theme.colors.gradientSuccess};
  color: ${({ theme }) => theme.colors.textOnPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  
  svg {
    margin-right: 0.5rem;
  }
`;

const ReviewContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xl};
  align-items: flex-start;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.lg};
  }
`;

const CertificateSection = styled.div`
  flex: 0 0 auto;
`;

const ReviewSection = styled.div`
  flex: 1;
`;

const StarRating = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const StarContainer = styled.div`
  display: flex;
  align-items: center;
`;

const RatingText = styled.div`
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  line-height: 1.5;
`;

const RatingDescription = styled.span`
  margin-left: 12px;
  color: ${({ theme }) => theme.colors.warningLight};
  font-size: 14px;
  transition: all 0.3s ease;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(5px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const Star = styled.button`
  background: none;
  border: none;
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  cursor: pointer;
  color: ${({ filled, theme, hoveredOver }) => 
    filled ? theme.colors.warning : 
    hoveredOver ? 'rgba(255, 255, 255, 0.4)' : 
    'rgba(255, 255, 255, 0.2)'};
  transition: all ${({ theme }) => theme.transitions.fast};
  padding: 0;
  outline: none;
  transform: ${({ filled, hoveredOver }) => 
    filled || hoveredOver ? 'scale(1.1)' : 'scale(1)'};
  
  &:hover {
    transform: scale(1.2);
    color: ${({ theme }) => theme.colors.warningLight};
  }
  
  &:focus {
    color: ${({ theme }) => theme.colors.warning};
    transform: scale(1.1);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
  resize: vertical;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  background: rgba(15, 23, 42, 0.3);
  color: rgba(255, 255, 255, 0.9);
  transition: all 0.3s ease;
  max-height: 250px;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
    background: rgba(15, 23, 42, 0.4);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(15, 23, 42, 0.4);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    min-height: 80px;
  }
`;

const WorkshopButton = styled(Button)`
  white-space: nowrap;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  letter-spacing: 0.5px;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.lg}`};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
    filter: brightness(1.1);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(120deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s ease;
  }
  
  &:hover:after {
    transform: translateX(100%);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    
    &:after {
      display: none;
    }
  }
`;

const ThankYouMessage = styled.div`
  color: ${({ theme }) => theme.colors.success};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  background: rgba(16, 185, 129, 0.1);
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid rgba(16, 185, 129, 0.2);
  
  svg {
    margin-right: 0.5rem;
    flex-shrink: 0;
  }
`;

const SavedReview = styled.div`
  background: rgba(15, 23, 42, 0.2);
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
  animation: fadeIn 0.5s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  &:hover {
    background: rgba(15, 23, 42, 0.3);
    border-color: rgba(255, 255, 255, 0.1);
  }
`;

const SavedRating = styled.div`
  color: ${({ theme }) => theme.colors.warning};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  display: flex;
  align-items: center;
  
  span {
    margin-left: 10px;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: rgba(255, 255, 255, 0.7);
  }
`;

const SavedFeedback = styled.p`
  color: ${({ theme }) => theme.colors.textOnDark};
  opacity: 0.8;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin: 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
  background: rgba(255, 255, 255, 0.03);
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border-left: 3px solid rgba(255, 255, 255, 0.1);
`;

const CertificateContainer = styled.div`
  margin-top: ${({ isVisible }) => isVisible ? '1.5rem' : '0'};
  max-height: ${({ isVisible }) => isVisible ? '500px' : '0'};
  overflow: hidden;
  transition: all ${({ theme }) => theme.transitions.normal};
`;

const Certificate = styled.div`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.15));
  backdrop-filter: blur(10px);
  border: 2px solid rgba(196, 169, 98, 0.5);
  padding: ${({ theme }) => theme.spacing.xxl} ${({ theme }) => theme.spacing.xl};
  text-align: center;
  position: relative;
  margin: ${({ theme }) => theme.spacing.md} 0;
  font-family: 'Serif';
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 10px;
    left: 10px;
    right: 10px;
    bottom: 10px;
    border: 1px solid rgba(230, 213, 167, 0.3);
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.xl};
  }
`;

const CertificateTitle = styled.h2`
  color: ${({ theme }) => theme.colors.textOnDark};
  font-size: ${({ theme }) => theme.typography.fontSize.xxl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  font-family: 'Serif';
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const CertificateText = styled.p`
  color: ${({ theme }) => theme.colors.textOnDark};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  line-height: ${({ theme }) => theme.typography.lineHeight.loose};
  margin: ${({ theme }) => theme.spacing.sm} 0;
`;

const CertificateName = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  color: ${({ theme }) => theme.colors.textOnDark};
  margin: ${({ theme }) => theme.spacing.xl} 0;
  font-style: italic;
`;

const CertificateDate = styled.div`
  color: rgba(255, 255, 255, 0.8);
  margin-top: ${({ theme }) => theme.spacing.xl};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
`;

const CompletedWorkshopCard = ({ workshop, completedData, onViewCertificate, onRate }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [tempRating, setTempRating] = useState(0); // Store temporary rating for UI display
  const [tempFeedback, setTempFeedback] = useState(''); // Store temporary feedback for UI display
  const [showCertificate, setShowCertificate] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) return;
    
    setSubmitting(true);
    
    try {
      // Save temporary values for display purposes only
      setTempRating(rating);
      setTempFeedback(feedback);
      
      // Call onRate but the parent component won't actually save the data
      await onRate(workshop.id, { rating, feedback });
      
      // Show success state
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting review:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleCertificate = () => {
    setShowCertificate(!showCertificate);
  };

  return (
    <Card>
      <CardContent>
        <div>
          <CompletedBadge>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            Completed
          </CompletedBadge>
          
          <Title>{workshop.title}</Title>
          <CompletedDate>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            Completed on {new Date(completedData.date || workshop.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </CompletedDate>
          
          {/* Display workshop details */}
          {workshop.description && (
            <WorkshopDetails>
              <h4>About this Workshop</h4>
              <p>{workshop.description}</p>
            </WorkshopDetails>
          )}
        </div>

        <ReviewContainer>
          <CertificateSection>            <WorkshopButton onClick={onViewCertificate}>
              Download Certificate
            </WorkshopButton>
          </CertificateSection>

          <ReviewSection>
            {!submitted ? (
              <>
                <StarRating>
                  <RatingText>
                    What did you think about this workshop? (Click to rate)
                  </RatingText>
                  <StarContainer>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        filled={star <= rating}
                        hoveredOver={star <= hoverRating}
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        type="button"
                        aria-label={`Rate ${star} out of 5 stars`}
                      >
                        ★
                      </Star>
                    ))}
                    {(rating > 0 || hoverRating > 0) && (
                      <RatingDescription>
                        {hoverRating > 0 ? 
                          ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][hoverRating] : 
                          `${rating}/5 - ${['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][rating]}`}
                      </RatingDescription>
                    )}
                  </StarContainer>
                </StarRating>
                <Textarea
                  placeholder="Share your feedback about this workshop (optional)"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  aria-label="Workshop feedback"
                />
                <WorkshopButton 
                  onClick={handleSubmit}
                  disabled={rating === 0 || submitting}
                  type="button"
                >
                  {submitting ? 'Submitting...' : 'Submit Review'}
                </WorkshopButton>
              </>
            ) : (
              <>
                <ThankYouMessage>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                  Thank you for your review! (This is for display purposes only)
                </ThankYouMessage>
                <SavedReview>
                  {/* Use temporary rating for newly submitted reviews */}
                  <SavedRating>
                    {'★'.repeat(tempRating)}{'☆'.repeat(5 - tempRating)}
                    <span>{tempRating}/5 - {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][tempRating]}</span>
                  </SavedRating>
                  {/* Use temporary feedback for newly submitted reviews */}
                  {tempFeedback && (
                    <SavedFeedback>"{tempFeedback}"</SavedFeedback>
                  )}
                </SavedReview>
                <WorkshopButton 
                  onClick={() => {
                    setSubmitted(false);
                    setRating(0);
                    setFeedback('');
                  }}
                  style={{ marginTop: '1rem' }}
                >
                  Submit Another Review
                </WorkshopButton>
              </>
            )}
          </ReviewSection>
        </ReviewContainer>

        <CertificateContainer isVisible={showCertificate}>
          <Certificate>
            <CertificateTitle>Certificate of Completion</CertificateTitle>
            <CertificateText>This certifies that</CertificateText>
            <CertificateName>John Doe</CertificateName>
            <CertificateText>has successfully completed the workshop</CertificateText>
            <CertificateName>{workshop.title}</CertificateName>
            <CertificateText>conducted by</CertificateText>
            <CertificateText>{workshop.presenter}</CertificateText>
            <CertificateDate>
              Completed on {new Date(completedData.date || workshop.date || new Date()).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </CertificateDate>
          </Certificate>
        </CertificateContainer>
      </CardContent>
    </Card>
  );
};

export default CompletedWorkshopCard;
